import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { analyticsApi } from '@/api';
import {
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatTile,
} from '@/components/ui';
import { ExportMenu } from '@/features/analytics/ExportMenu';
import { HiringFunnel } from '@/features/analytics/HiringFunnel';
import { PeriodPicker } from '@/features/analytics/PeriodPicker';
import { SegmentList } from '@/features/analytics/SegmentList';
import { TrendChart } from '@/features/analytics/TrendChart';
import { GROWTH_SERIES } from '@/features/analytics/series';
import { count, days, percent } from '@/features/analytics/format';
import { errorMessage } from '@/hooks/useApiError';

const ACCOUNT_LABELS = {
  ACTIVE: 'Active',
  PENDING_VERIFICATION: 'Awaiting email verification',
  INACTIVE: 'Inactive',
  SUSPENDED: 'Suspended',
  BANNED: 'Banned',
  DELETED: 'Deleted',
};

const EXPORT_SHEETS = [
  {
    key: 'growth',
    label: 'Registrations over time',
    hint: 'Students, companies, listings, applications',
  },
  {
    key: 'platform-funnel',
    label: 'Application funnel',
    hint: 'Each stage with both drop-off rates',
  },
  {
    key: 'listings-summary',
    label: 'Listing performance',
    hint: 'Fill rate, time to fill, applications each',
  },
];

export function AdminAnalyticsPage() {
  const [period, setPeriod] = useState(90);

  const {
    data: overview,
    isPending,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['platform-analytics', 'overview', period],
    queryFn: () => analyticsApi.getPlatformOverview(period),
  });

  const { data: growth } = useQuery({
    queryKey: ['platform-analytics', 'growth', period],
    queryFn: () => analyticsApi.getPlatformGrowth(period),
  });

  const { data: segments } = useQuery({
    queryKey: ['platform-analytics', 'segments', period],
    queryFn: () => analyticsApi.getPlatformSegments(period),
  });

  const { data: listings } = useQuery({
    queryKey: ['platform-analytics', 'listings', period],
    queryFn: () => analyticsApi.getListingReport(period),
  });

  if (isPending) return <LoadingState label="Reading the platform…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const { totals, activation, speed } = overview;
  const accounts = Object.entries(overview.accounts ?? {}).filter(([, value]) => value > 0);

  return (
    <div>
      <PageHeader
        title="Platform analytics"
        description="Whether the platform is being used, and by whom."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <PeriodPicker value={period} onChange={setPeriod} />
            <ExportMenu sheets={EXPORT_SHEETS} days={period} />
          </div>
        }
      />

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Students"
          value={count(totals.students)}
          hint={`${count(totals.new_students)} joined in this period`}
        />
        <StatTile
          label="Companies"
          value={count(totals.companies)}
          hint={`${count(totals.new_companies)} joined in this period`}
        />
        <StatTile
          label="Live internships"
          value={count(totals.live_internships)}
          hint="Published and open right now"
        />
        <StatTile
          label="Hires"
          value={count(totals.hires)}
          hint={`From ${count(totals.applications)} applications`}
        />
      </div>

      <div className="mb-6 grid gap-6 lg:grid-cols-[1.2fr_1fr]">
        <Card>
          <CardHeader
            title="Platform funnel"
            description="Every application received in this period, and how far it got."
          />
          <HiringFunnel stages={overview.funnel} emptyLabel="No applications in this period" />
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader
              title="Activation"
              description="Registering is not using. These are the accounts that did something."
            />
            <dl className="space-y-3 text-sm">
              <Metric
                label="Students who applied"
                value={percent(activation.student_activation_rate)}
                hint={`${count(activation.students_who_applied)} of ${count(totals.students)}`}
              />
              <Metric
                label="Companies who published"
                value={percent(activation.company_activation_rate)}
                hint={`${count(activation.companies_who_published)} of ${count(totals.companies)}`}
              />
              <Metric
                label="Applications answered"
                value={percent(speed.response_rate)}
                hint={`Median first response ${days(speed.median_days_to_first_response)}`}
              />
            </dl>
          </Card>

          <Card>
            <CardHeader title="Account standing" description="Every account, by status." />
            {accounts.length === 0 ? (
              <EmptyState compact title="No accounts yet" />
            ) : (
              <dl className="space-y-2 text-sm">
                {accounts.map(([status, value]) => (
                  <Metric
                    key={status}
                    label={ACCOUNT_LABELS[status] ?? status}
                    value={count(value)}
                  />
                ))}
              </dl>
            )}
          </Card>
        </div>
      </div>

      {listings && (
        <Card className="mb-6">
          <CardHeader
            title="How listings do"
            description="The listings published in this period, followed wherever they got to."
          />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile
              label="Published"
              value={count(listings.published)}
              hint={`${count(listings.without_applications)} have had no applications`}
            />
            <StatTile
              label="Filled"
              value={percent(listings.fill_rate)}
              hint={`${count(listings.filled)} hired somebody`}
            />
            <StatTile
              label="Applications each"
              value={listings.median_applications ?? '—'}
              hint="Median across the period's listings"
            />
            <StatTile
              label="Time to fill"
              value={days(listings.median_days_to_fill)}
              hint={`First application after ${days(listings.median_days_to_first_application)}`}
            />
          </div>
          {/* A listing published yesterday has not had time to hire anybody, so
              a short window makes this look worse than it is. Saying so is
              cheaper than watching somebody act on a 90-day fill rate. */}
          <p className="text-ink-muted mt-4 text-xs">
            These follow a cohort, so the fill rate is pessimistic over a short window — recent
            listings have not had time to hire anyone. Widen the period for a figure worth
            comparing.
          </p>
        </Card>
      )}

      <Card className="mb-6">
        <CardHeader title="Growth" description="Signups, listings and applications over time." />
        <TrendChart
          points={growth?.data ?? []}
          series={GROWTH_SERIES}
          granularity={growth?.meta?.granularity}
          emptyLabel="Nothing was created in this period"
        />
      </Card>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card>
          <CardHeader title="Most active companies" description="By applications received." />
          <SegmentList
            items={segments?.top_companies ?? []}
            emptyLabel="No applications in this period"
            secondaryLabel="hired"
          />
        </Card>
        <Card>
          <CardHeader title="Universities" description="Where applicants study." />
          <SegmentList
            items={segments?.top_universities ?? []}
            emptyLabel="No applicants with a university on file"
          />
        </Card>
        <Card>
          <CardHeader title="Categories in demand" description="What students apply for." />
          <SegmentList
            items={segments?.top_categories ?? []}
            emptyLabel="No categorised applications"
          />
        </Card>
      </div>
    </div>
  );
}

function Metric({ label, value, hint }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="min-w-0">
        <span className="text-ink">{label}</span>
        {hint && <span className="text-ink-muted block text-xs">{hint}</span>}
      </dt>
      <dd className="text-ink shrink-0 font-medium">{value}</dd>
    </div>
  );
}
